import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { Button, Form, Input, Popconfirm, Select, Switch, Table, Tooltip } from 'antd'
import { DeleteOutlined, PlusOutlined, PlusSquareOutlined, CaretRightOutlined, CaretDownOutlined } from '@ant-design/icons'

interface Ret00caBatchTableProps {
  paramScope: 'request' | 'response'
  initialItems?: any[]
  onDraftChange?: (items: any[]) => void
}

export interface Ret00caBatchTableRef {
  addRow: (parentKey?: string) => void
  getItems: () => any[]
}

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean
  dataIndex: string
  title: string
  inputType: 'text' | 'select' | 'switch'
  record: any
  children: React.ReactNode
  options?: { label: string; value: any }[]
  required?: boolean
  __depth?: number
  __hasChildren?: boolean
  __isExpanded?: boolean
  __onToggleExpand?: () => void
}

const isMeaningfulDraftRow = (item: any) =>
  !!item?.name || !!item?.display_name || !!item?.description

const EditableCell: React.FC<EditableCellProps> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  children,
  options,
  required = false,
  __depth = 0,
  __hasChildren = false,
  __isExpanded = false,
  __onToggleExpand,
  ...restProps
}) => {
  const inputNode = (() => {
    switch (inputType) {
      case 'select':
        return <Select style={{ width: '100%' }} options={options} />
      case 'switch':
        return <Switch />
      default:
        return <Input />
    }
  })()

  const indentUnit = 22

  return (
    <td {...restProps}>
      {editing ? (
        <div className='flex items-center gap-2'>
          {dataIndex === 'name' && (
            <>
              <span
                style={{
                  display: 'inline-block',
                  width: __depth * indentUnit,
                  flexShrink: 0,
                  height: 1,
                }}
              />
              <span
                onClick={__hasChildren ? __onToggleExpand : undefined}
                style={{
                  display: 'inline-flex',
                  width: 16,
                  flexShrink: 0,
                  justifyContent: 'center',
                  cursor: __hasChildren ? 'pointer' : 'default',
                  color: __hasChildren ? undefined : 'transparent',
                  userSelect: 'none',
                }}
              >
                {__hasChildren ? (__isExpanded ? <CaretDownOutlined /> : <CaretRightOutlined />) : <CaretRightOutlined />}
              </span>
            </>
          )}
          <Form.Item
            name={[record.key, dataIndex]}
            style={{ margin: 0, flex: 1, minWidth: 0 }}
            initialValue={record[dataIndex]}
            valuePropName={inputType === 'switch' ? 'checked' : 'value'}
            rules={required ? [({ getFieldValue }) => ({
              validator(_, value) {
                const rowValues = getFieldValue(record.key) || {}
                const shouldRequireField = isMeaningfulDraftRow({
                  name: rowValues.name ?? record.name,
                  display_name: rowValues.display_name ?? record.display_name,
                  description: rowValues.description ?? record.description,
                })

                if (!shouldRequireField) {
                  return Promise.resolve()
                }

                if (value === undefined || value === null || value === '') {
                  return Promise.reject(new Error(`请输入${title}`))
                }

                return Promise.resolve()
              },
            })] : []}
          >
            {inputNode}
          </Form.Item>
        </div>
      ) : children}
    </td>
  )
}

const createEmptyRow = (paramScope: 'request' | 'response', rowParamScope?: string) => ({
  key: `new_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  name: '',
  display_name: '',
  parent_ref: undefined,
  param_scope: paramScope === 'response' ? 'response' : (rowParamScope || 'query'),
  data_type: 'string',
  required: true,
  enabled: true,
  description: '',
})

const isMeaningfulRow = (item: any) => isMeaningfulDraftRow(item)

const findChildInsertIndex = (rows: any[], parentKey: string) => {
  const parentIndex = rows.findIndex((item) => item.key === parentKey)

  if (parentIndex < 0) {
    return rows.length
  }

  const descendantRefs = new Set([`temp:${parentKey}`])
  let insertIndex = parentIndex + 1

  while (insertIndex < rows.length) {
    const currentRow = rows[insertIndex]

    if (currentRow.parent_ref && descendantRefs.has(currentRow.parent_ref)) {
      descendantRefs.add(`temp:${currentRow.key}`)
      insertIndex += 1
      continue
    }

    break
  }

  return insertIndex
}

const buildInitialRows = (items: any[], paramScope: 'request' | 'response') => {
  if (items.length === 0) {
    return [createEmptyRow(paramScope)]
  }

  const keyById = new Map<number, string>()

  items.forEach((item) => {
    if (item.id) {
      keyById.set(item.id, `row_${item.id}`)
    }
  })

  return items.map((item, index) => {
    const rowKey = item.id ? keyById.get(item.id) : `row_${Date.now()}_${index}_${Math.random().toString(36).slice(2, 6)}`
    const parentKey = item.parent_id ? keyById.get(item.parent_id) : undefined

    return {
      key: rowKey,
      name: item.name || '',
      display_name: item.display_name || '',
      parent_ref: parentKey ? `temp:${parentKey}` : undefined,
      param_scope: paramScope === 'response' ? 'response' : (item.param_scope || 'query'),
      data_type: item.data_type || 'string',
      required: item.required ?? true,
      enabled: item.enabled ?? true,
      description: item.description || '',
    }
  })
}

const Ret00caBatchTable = forwardRef<Ret00caBatchTableRef, Ret00caBatchTableProps>(function Ret00caBatchTable(
  { paramScope, initialItems = [], onDraftChange },
  ref
): JSX.Element {
  const [form] = Form.useForm()
  const [data, setData] = useState<any[]>(() => buildInitialRows(initialItems, paramScope))
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([])

  const emitDraftChange = (_ignored?: any) => {
    const values = form.getFieldsValue() as Record<string, any>
    const mapped = data
      .map((item, index) => {
        const rowFormValues = values[item.key] || {}
        return {
          name: rowFormValues.name ?? item.name ?? '',
          display_name: rowFormValues.display_name ?? item.display_name ?? '',
          data_type: rowFormValues.data_type ?? item.data_type ?? 'string',
          required: rowFormValues.required ?? item.required ?? true,
          enabled: rowFormValues.enabled ?? item.enabled ?? true,
          description: rowFormValues.description ?? item.description ?? '',
          temp_key: item.key,
          parent_ref: rowFormValues.parent_ref ?? item.parent_ref,
          param_scope: paramScope === 'response' ? 'response' : (rowFormValues.param_scope || item.param_scope || 'query'),
          sort_no: index,
        }
      })

    const tempKeyToItem = new Map(mapped.map((m) => [m.temp_key, m]))
    const meaningfulKeys = new Set(mapped.filter(isMeaningfulRow).map((m) => m.temp_key))
    const keepKeys = new Set(meaningfulKeys)
    let keepChanged = true
    while (keepChanged) {
      keepChanged = false
      keepKeys.forEach((key) => {
        const item = tempKeyToItem.get(key as string)
        const parentRef = item?.parent_ref?.startsWith('temp:') ? item.parent_ref.replace('temp:', '') : undefined
        if (parentRef && tempKeyToItem.has(parentRef) && !keepKeys.has(parentRef)) {
          keepKeys.add(parentRef)
          keepChanged = true
        }
      })
    }

    const keptMapped = mapped.filter((m) => keepKeys.has(m.temp_key))
    const items = keptMapped
      .map((item) => {
        const parentRef = item.parent_ref as string | undefined
        const transformedItem: any = { ...item }

        delete transformedItem.parent_ref

        if (parentRef?.startsWith('temp:')) {
          transformedItem.parent_key = parentRef.replace('temp:', '')
        }

        return transformedItem
      })

    console.log(`[DEBUG Ret00caBatchTable:${paramScope}] emitDraftChange`, {
      dataLength: data.length,
      dataNames: data.map(d => ({ key: d.key, name: d.name, parent_ref: d.parent_ref })),
      formValuesKeys: Object.keys(values),
      mapped: mapped.map(m => ({ temp_key: m.temp_key, name: m.name, meaningful: isMeaningfulRow(m) })),
      meaningfulKeys: Array.from(meaningfulKeys),
      keepKeys: Array.from(keepKeys),
      keptMappedLength: keptMapped.length,
      finalItems: items,
    })

    onDraftChange?.(items)
  }

  useEffect(() => {
    const currentValues = form.getFieldsValue()
    const nextValues = data.reduce((result, item) => {
      const rowValues = currentValues[item.key] || {}
      result[item.key] = {
        name: rowValues.name ?? item.name ?? '',
        display_name: rowValues.display_name ?? item.display_name ?? '',
        parent_ref: rowValues.parent_ref ?? item.parent_ref,
        param_scope: rowValues.param_scope ?? item.param_scope ?? 'query',
        data_type: rowValues.data_type ?? item.data_type ?? 'string',
        required: rowValues.required ?? item.required ?? true,
        enabled: rowValues.enabled ?? item.enabled ?? true,
        description: rowValues.description ?? item.description ?? '',
      }
      return result
    }, {} as Record<string, any>)

    form.setFieldsValue(nextValues)
    emitDraftChange(nextValues)
  }, [data, form])

  useEffect(() => {
    console.log(`[DEBUG Ret00caBatchTable:${paramScope}] initialItems effect triggered`, {
      initialItemsLength: initialItems.length,
      initialItems: initialItems.map((i: any) => ({ id: i.id, name: i.name, parent_id: i.parent_id })),
    })
    setData(buildInitialRows(initialItems, paramScope))
  }, [initialItems, paramScope])

  useEffect(() => {
    setExpandedRowKeys(data.map((item) => item.key))
  }, [data])

  const deleteRow = (key: string) => {
    setData((current) => {
      const targetRow = current.find((item) => item.key === key)
      const adoptParentRef = targetRow?.parent_ref

      console.log(`[DEBUG Ret00caBatchTable:${paramScope}] deleteRow called`, {
        deleteKey: key,
        adoptParentRef,
        beforeData: current.map(d => ({ key: d.key, name: d.name, parent_ref: d.parent_ref })),
      })

      const nextData = current
        .filter((item) => item.key !== key)
        .map((item) => {
          if (item.parent_ref === `temp:${key}`) {
            return { ...item, parent_ref: adoptParentRef }
          }
          return item
        })

      console.log(`[DEBUG Ret00caBatchTable:${paramScope}] deleteRow result`, {
        afterData: nextData.map(d => ({ key: d.key, name: d.name, parent_ref: d.parent_ref })),
        fallbackEmpty: nextData.length === 0,
      })

      return nextData.length > 0 ? nextData : [createEmptyRow(paramScope)]
    })
  }

  const addRow = (parentKey?: string) => {
    console.log(`[DEBUG Ret00caBatchTable:${paramScope}] addRow called`, {
      parentKey,
      caller: new Error().stack?.split('\n').slice(0, 3).join('\n'),
    })
    setData((current) => {
      if (!parentKey) {
        const nextData = [...current, createEmptyRow(paramScope)]
        console.log(`[DEBUG Ret00caBatchTable:${paramScope}] addRow no-parent`, {
          nextData: nextData.map(d => ({ key: d.key, parent_ref: d.parent_ref })),
        })
        return nextData
      }

      const parentRow = current.find((item) => item.key === parentKey)
      const nextRow = {
        ...createEmptyRow(paramScope, parentRow?.param_scope),
        parent_ref: `temp:${parentKey}`,
      }
      const insertIndex = findChildInsertIndex(current, parentKey)

      const nextData = [
        ...current.slice(0, insertIndex),
        nextRow,
        ...current.slice(insertIndex),
      ]
      console.log(`[DEBUG Ret00caBatchTable:${paramScope}] addRow with parent`, {
        parentKey,
        parentRefOfParent: parentRow?.parent_ref,
        newRowKey: nextRow.key,
        newRowParentRef: nextRow.parent_ref,
        nextData: nextData.map(d => ({ key: d.key, parent_ref: d.parent_ref })),
      })
      return nextData
    })

    if (parentKey) {
      setExpandedRowKeys((current) => Array.from(new Set([...current, parentKey])))
    }
  }

  const addSiblingRow = (record: any) => {
    console.log(`[DEBUG Ret00caBatchTable:${paramScope}] addSiblingRow called`, {
      record: { key: record?.key, parent_ref: record?.parent_ref, param_scope: record?.param_scope },
      caller: new Error().stack?.split('\n').slice(0, 3).join('\n'),
    })
    setData((current) => {
      const nextRow = {
        ...createEmptyRow(paramScope, record.param_scope),
        parent_ref: record.parent_ref,
      }
      const insertIndex = findChildInsertIndex(current, record.key)

      const nextData = [
        ...current.slice(0, insertIndex),
        nextRow,
        ...current.slice(insertIndex),
      ]
      console.log(`[DEBUG Ret00caBatchTable:${paramScope}] addSiblingRow result`, {
        siblingOfKey: record.key,
        siblingParentRef: record.parent_ref,
        newRowKey: nextRow.key,
        newRowParentRef: nextRow.parent_ref,
        nextData: nextData.map(d => ({ key: d.key, parent_ref: d.parent_ref })),
      })
      return nextData
    })
  }

  useImperativeHandle(ref, () => ({
    addRow,
    getItems: () => {
      const values = form.getFieldsValue() as Record<string, any>
      const mapped = data
        .map((item, index) => {
          const rowFormValues = values[item.key] || {}
          return {
            name: rowFormValues.name ?? item.name ?? '',
            display_name: rowFormValues.display_name ?? item.display_name ?? '',
            data_type: rowFormValues.data_type ?? item.data_type ?? 'string',
            required: rowFormValues.required ?? item.required ?? true,
            enabled: rowFormValues.enabled ?? item.enabled ?? true,
            description: rowFormValues.description ?? item.description ?? '',
            temp_key: item.key,
            parent_ref: rowFormValues.parent_ref ?? item.parent_ref,
            param_scope: paramScope === 'response' ? 'response' : (rowFormValues.param_scope || item.param_scope || 'query'),
            sort_no: index,
          }
        })

      const tempKeyToItem = new Map(mapped.map((m) => [m.temp_key, m]))
      const meaningfulKeys = new Set(mapped.filter(isMeaningfulRow).map((m) => m.temp_key))
      const keepKeys = new Set(meaningfulKeys)
      let keepChanged = true
      while (keepChanged) {
        keepChanged = false
        keepKeys.forEach((key) => {
          const item = tempKeyToItem.get(key as string)
          const parentRef = item?.parent_ref?.startsWith('temp:') ? item.parent_ref.replace('temp:', '') : undefined
          if (parentRef && tempKeyToItem.has(parentRef) && !keepKeys.has(parentRef)) {
            keepKeys.add(parentRef)
            keepChanged = true
          }
        })
      }

      return mapped
        .filter((m) => keepKeys.has(m.temp_key))
        .map((item) => {
          const parentRef = item.parent_ref as string | undefined
          const transformedItem: any = { ...item }
          delete transformedItem.parent_ref
          if (parentRef?.startsWith('temp:')) {
            transformedItem.parent_key = parentRef.replace('temp:', '')
          }
          return transformedItem
        })
    },
  }))

  const tableData = useMemo(() => {
    const childrenMap = new Map<string, string[]>()
    data.forEach((item) => {
      const parentKey = item.parent_ref?.startsWith('temp:') ? item.parent_ref.replace('temp:', '') : undefined
      if (parentKey) {
        const arr = childrenMap.get(parentKey) || []
        arr.push(item.key)
        childrenMap.set(parentKey, arr)
      }
    })

    const depthMap = new Map<string, number>()
    const calcDepth = (key: string, chain: Set<string>): number => {
      if (depthMap.has(key)) return depthMap.get(key)!
      if (chain.has(key)) return 0
      const item = data.find((d) => d.key === key)
      const parentKey = item?.parent_ref?.startsWith('temp:') ? item.parent_ref.replace('temp:', '') : undefined
      if (!parentKey) {
        depthMap.set(key, 0)
        return 0
      }
      chain.add(key)
      const d = calcDepth(parentKey, chain) + 1
      chain.delete(key)
      depthMap.set(key, d)
      return d
    }
    data.forEach((item) => calcDepth(item.key, new Set()))

    const expandedSet = new Set(expandedRowKeys)
    const isAncestorsExpanded = (key: string): boolean => {
      const item = data.find((d) => d.key === key)
      const parentKey = item?.parent_ref?.startsWith('temp:') ? item.parent_ref.replace('temp:', '') : undefined
      if (!parentKey) return true
      if (!expandedSet.has(parentKey)) return false
      return isAncestorsExpanded(parentKey)
    }

    const visible = data
      .filter((item) => isAncestorsExpanded(item.key))
      .map((item) => ({
        ...item,
        __depth: depthMap.get(item.key) ?? 0,
        __hasChildren: !!childrenMap.get(item.key)?.length,
        __isExpanded: expandedSet.has(item.key),
      }))

    return visible
  }, [data, expandedRowKeys])

  const toggleExpand = (key: React.Key) => {
    setExpandedRowKeys((current) => {
      const set = new Set(current)
      if (set.has(key)) {
        set.delete(key)
      } else {
        set.add(key)
      }
      return Array.from(set)
    })
  }

  const columns = useMemo(() => {
    const requestScopeColumn = paramScope === 'request'
      ? [{
          title: '参数位置',
          dataIndex: 'param_scope',
          key: 'param_scope',
          width: 140,
          editable: true,
          inputType: 'select' as const,
          required: true,
          options: [
            { label: '请求头', value: 'header' },
            { label: 'Query 参数', value: 'query' },
            { label: 'Path 参数', value: 'path' },
            { label: '请求体', value: 'body' },
          ],
        }]
      : []

    return [
      ...requestScopeColumn,
      {
        title: '参数名',
        dataIndex: 'name',
        key: 'name',
        width: paramScope === 'response' ? 240 : 180,
        editable: true,
        inputType: 'text' as const,
        required: true,
      },
      {
        title: '中文名',
        dataIndex: 'display_name',
        key: 'display_name',
        width: 160,
        editable: true,
        inputType: 'text' as const,
      },
      {
        title: '数据类型',
        dataIndex: 'data_type',
        key: 'data_type',
        width: 140,
        editable: true,
        inputType: 'select' as const,
        required: true,
        options: [
          { label: 'string', value: 'string' },
          { label: 'integer', value: 'integer' },
          { label: 'decimal', value: 'decimal' },
          { label: 'boolean', value: 'boolean' },
          { label: 'object', value: 'object' },
          { label: 'array', value: 'array' },
          { label: 'file', value: 'file' },
          { label: 'float', value: 'float' },
        ],
      },
      {
        title: '必填',
        dataIndex: 'required',
        key: 'required',
        width: 90,
        editable: true,
        inputType: 'switch' as const,
        render: (value: boolean) => value ? '是' : '否',
      },
      {
        title: '启用',
        dataIndex: 'enabled',
        key: 'enabled',
        width: 90,
        editable: true,
        inputType: 'switch' as const,
        render: (value: boolean) => value ? '是' : '否',
      },
      {
        title: '说明',
        dataIndex: 'description',
        key: 'description',
        editable: true,
        inputType: 'text' as const,
      },
      {
        title: '操作',
        key: 'operation',
        width: 120,
        render: (_: any, record: any) => (
          <>
            <Tooltip title='增加同级'>
              <Button
                type='text'
                size='small'
                icon={<PlusOutlined />}
                onClick={() => addSiblingRow(record)}
              />
            </Tooltip>
            <Tooltip title='增加子级'>
              <Button
                type='text'
                size='small'
                icon={<PlusSquareOutlined />}
                onClick={() => addRow(record.key)}
              />
            </Tooltip>
            <Popconfirm title='确定删除这行吗？' onConfirm={() => deleteRow(record.key)}>
              <Button type='text' size='small' danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </>
        ),
      },
    ]
  }, [paramScope, data])

  const mergedColumns = columns.map((col: any) => {
    if (!col.editable) {
      return col
    }

    return {
      ...col,
      onCell: (record: any) => ({
        record,
        editing: true,
        dataIndex: col.dataIndex,
        title: col.title,
        inputType: col.inputType,
        options: col.dataIndex === 'parent_ref'
          ? (col.options || []).filter((option: any) => option.value !== `temp:${record.key}`)
          : col.options,
        required: col.required,
        ...(col.dataIndex === 'name' ? {
          __depth: record.__depth ?? 0,
          __hasChildren: !!record.__hasChildren,
          __isExpanded: !!record.__isExpanded,
          __onToggleExpand: () => toggleExpand(record.key),
        } : {}),
      }),
    }
  })

  return (
    <div className='mb-4'>
      <Form
        form={form}
        component={false}
        onValuesChange={(_, values) => {
          emitDraftChange(values)
        }}
      >
        <Table
          rowKey='key'
          components={{ body: { cell: EditableCell } }}
          columns={mergedColumns as any}
          dataSource={tableData}
          pagination={false}
          size='small'
          bordered
          scroll={{ x: 960 }}
        />
      </Form>
    </div>
  )
})

export default Ret00caBatchTable
